import { SearchParams } from "@/shared/environment/SearchParams";
import { Goto } from "@/shared/navigation/Goto";
import { nanoSecToSecStr } from "@/shared/time";

import type { RobotoUriType } from "./magicLinks";

/**
 * The `roboto://` types whose page is reachable from the ID alone (plus, for
 * `workspace`, the optional `t` the URI carries), so they resolve inline in a
 * chat message with no fetch and no loading state.
 *
 * `topic`, `msgpath`, `file`, and `dataset` are absent because they need a
 * lookup first: `topic` and `msgpath` resolve the associated file (and a panel
 * layout), `file` branches on ingestion status, and `dataset` fetches for its
 * tooltip. `RobotoUriRedirect` and `VizExternalLink` route those.
 */
const ENTITY_ROUTES = {
  // Wrapped so that the URI's `t` does not reach the builders' tab parameter.
  collection: (id: string) => Goto.createCollectionRoute(id),
  device: (id: string) => Goto.createDeviceRoute(id),
  // Wrapped so that the URI's `t` does not reach the builder, which would
  // seek the visualizer to it as the event's start time.
  event: (id: string) => Goto.createEventVisualizerRoute(id),
  invocation: Goto.createInvocationRoute,
  org: Goto.createOrgRoute,
  session: (id: string) => Goto.createSessionRoute(id),
  trigger: Goto.createTriggerByIdRoute,
  // Saved workspace, opened in the visualizer at `t` (epoch ns on the wire,
  // fixed-point seconds in the route) when the URI carries one.
  workspace: (id: string, t?: bigint) =>
    `${Goto.createVisualizerRoute(id)}${
      t === undefined ? "" : `?${SearchParams.TIMESTAMP}=${nanoSecToSecStr(t)}`
    }`,
  // A layout carries no datasources, so the visualizer it opens has no data
  // to seek in; `t` is dropped.
  layout: (id: string) =>
    `${Goto.route.Visualize}?${new URLSearchParams({ [SearchParams.LAYOUT_ID]: id })}`,
} satisfies Partial<Record<RobotoUriType, (id: string, t?: bigint) => string>>;

/** A {@link RobotoUriType} that {@link robotoEntityPath} can resolve. */
export type DirectEntityType = keyof typeof ENTITY_ROUTES;

export function isDirectEntityType(
  type: RobotoUriType,
): type is DirectEntityType {
  return type in ENTITY_ROUTES;
}

/** Human-readable name for an entity type, for tooltips and error copy. */
export const ENTITY_TYPE_LABELS: Readonly<Record<DirectEntityType, string>> = {
  collection: "Collection",
  device: "Device",
  event: "Event",
  invocation: "Invocation",
  layout: "Layout",
  org: "Organization",
  session: "Session",
  trigger: "Trigger",
  workspace: "Workspace",
};

/**
 * In-app path for an ID-addressable entity, or `undefined` for a type this
 * cannot resolve. `id` is the entity's own id, decoded as `parseRobotoUri`
 * returns it; the path encodes it once. `t` (epoch ns) is honored only by
 * `workspace`; the rest ignore it.
 *
 * An empty `id` yields `undefined` rather than the collection *index* route:
 * a link the model wrote as a specific entity must not silently become a
 * navigation somewhere else.
 */
export function robotoEntityPath(
  type: RobotoUriType,
  id: string,
  t?: bigint,
): string | undefined {
  if (!isDirectEntityType(type) || id === "") {
    return undefined;
  }
  return ENTITY_ROUTES[type](id, t);
}
